<template>
  <v-container class="container-content">
    <form @keyup.enter.prevent="!successCreated ? onSubmit() : ''">
      <AppHeader v-if="!successCreated" header-class="col-12 col-md-8">
        {{
          $t(
            "signin:identifyYourself.heading",
            "Please identify yourself!",
            "Signin heading"
          )
        }}
      </AppHeader>

      <AppShowFeedback ref="showFeedback" />
      <div class="col-12 col-md-8">
        <div v-if="!successCreated" class="">
          <p v-if="isNewPerson" data-test="newPerson">
            {{
              $t(
                "signin:instructions.message1",
                "To open your account in KIPA, you need an active athlete account and your yearly licence (atleast E-licence) should be paid.",
                "Signin instructions message 1"
              )
            }}
            <br />
            {{
              $t(
                "signin:instructions.message2",
                "Please use the form below. We’ll send you an email with a link to (re)set your password.",
                "Signin instructions message 2"
              )
            }}
            <br />
            {{
              $t(
                "signin:instructions.message3",
                "If you do not remember your licence number or email in SENSE register, please retrieve this information from SENSE or contact KIPA support service.",
                "Signin instructions message 3"
              )
            }}
            <br />
            <br />
            {{
              $t(
                "signin:instructions.message4",
                "Organisers and administrators can sign in using their e-mail address that was used in the old KIPA site. If you do not remember your e-mail address, please contact KIPA support service",
                "Signin instructions message 4"
              )
            }}
          </p>
          <p v-else data-test="requestForm">
            {{
              $t(
                "signin:instructions.message5",
                "To (re)set your password, please use the form below. We’ll send you an email with a link to reset your password.",
                "Signin instructions message 5"
              )
            }}
            <br />
            {{
              $t(
                "signin:instructions.message3",
                "If you do not remember your licence number or email in SENSE register, please retrieve this information from SENSE or contact KIPA support service.",
                "Signin instructions message 3"
              )
            }}
          </p>
        </div>
        <div v-if="!successCreated" class="col-12 col-md-6 px-0">
          <v-text-field
            v-model="form.anyIdentity"
            :error-messages="validationErrors('anyIdentity')"
            :label="
              $t(
                'signin:signin.input.name.label',
                'E-mail or licence number',
                'Label of the signing field'
              )
            "
            required
            outlined
            class="mobile-max-width"
            @input="v$.form.anyIdentity.$touch()"
            @blur="v$.form.anyIdentity.$touch()"
          />

          <IdentityHints
            v-if="identityHints.length"
            v-model="form.identityHint"
            :identity-hints="identityHints"
            :loading="loading"
          />
        </div>

        <AppBtnsRow>
          <template #left="{ attributes }">
            <v-btn
              v-show="!successCreated"
              :to="{ name: 'LandingPage' }"
              :disabled="loading"
              v-bind="attributes"
            >
              {{ $t("common:button.cancel", "Cancel", "Cancel button") }}
            </v-btn>
          </template>
          <template #right="{ attributes }">
            <v-btn
              v-if="!successCreated"
              v-bind="attributes"
              color="accent"
              :loading="loading"
              :disabled="loading"
              @click="onSubmit"
              >{{ linkText }}</v-btn
            >
          </template>
        </AppBtnsRow>
      </div>
    </form>
  </v-container>
</template>

<script>
import gql from "fraql";
import appFormMixin from "@/mixins/appFormMixin.js";

import AppShowFeedback from "@AppShows/AppShowFeedback.vue";
import AppHeader from "@elements/AppHeader.vue";
import IdentityHints from "@elements/IdentityHints.vue";
import AppBtnsRow from "@elements/globals/AppBtnsRow.vue";

export default {
  name: "PersonRequestPasswordReset",
  components: { AppHeader, AppShowFeedback, IdentityHints, AppBtnsRow },
  mixins: [appFormMixin],
  data() {
    return {
      form: {},
      successCreated: false,
      isNewPerson: false,
      loading: false,
      identityHints: [],
      initForm: {
        anyIdentity: {
          required: true,
          minLength: 3
        }
      },
      appFormMixinValidationsConfVariable: "initForm"
    };
  },
  computed: {
    linkText() {
      if (this.$route.name == "JoinFederation") {
        return this.$t(
          "signin:button.sendmealink",
          "Send me a link",
          "Send me a link button"
        );
      } else {
        return this.$t(
          "signin:button.sendmealink1",
          "Send me a link to set password",
          "Send me a link button 2"
        );
      }
    }
  },
  created() {
    if (this.$route.name == "JoinFederation") {
      this.isNewPerson = true;
    }
  },
  methods: {
    validationErrors(fieldName) {
      return this.appFormMixinValidationErrorsForField(fieldName);
    },
    onSubmit() {
      this.loading = true;
      this.v$.form.$touch();
      if (this.v$.form.$invalid) {
        this.loading = false;
        this.$refs.showFeedback.open("warn", {
          text: this.$t(
            "signin:error.pleasereview",
            "Please review your input",
            "Signin error please review your input"
          )
        });
        return false;
      }
      // Mutation
      this.$apollo
        .mutate({
          mutation: gql`
            mutation personSendPasswordResetLink($anyIdentity: String!, $identityHint: String) {
              personSendPasswordResetLink(
                input: { anyIdentity: $anyIdentity, identityHint: $identityHint }
              ) {
                ${IdentityHints.fragments.identityHintsResetPassword}
                status
              }
            }
          `,
          variables: this.form
        })
        .then(({ data }) => {
          const { status, identityHints } = data.personSendPasswordResetLink;
          if (identityHints?.length) {
            this.identityHints = identityHints;
          }

          this.loading = false;
          if (this.isNewPerson) {
            switch (status) {
              case "ok":
                this.successCreated = true;
                this.$refs.showFeedback.open("success", {
                  text: this.$t(
                    "signin:feedback.message1",
                    "Welcome to KIPA! We have sent a link to set your password to your email. If you do not receive an email in 5 minutes, please check your spam folder and then start again. Please check whether your licence number or email are correct: email should be the same as your email in clubs register.",
                    "Signin feedback message 1"
                  ),
                  dismiss: false
                });
                break;
              case "pick_hint":
                this.$refs.showFeedback.open("warn", {
                  text: this.$t(
                    "signin:signinform.identityHint.alert",
                    "More than one account found, please choose the account based on hint below"
                  )
                });
                break;
              case "use_another_identity":
                this.$refs.showFeedback.open("error", {
                  text: this.$t(
                    "signin:feedback.message3",
                    "We are very sorry - something went wrong. We cannot send you a link to set a password. Your account in athletes register is not active or you do not have a unique email address in the system or you do not have a valid yearly licence. If your yearly licence is paid, but you cannot still access KIPA, please contact our SUPPORT service. Organisers and administrators can sign in using their e-mail address that was used in the old KIPA site. If you do not remember your e-mail address, please contact our SUPPORT service. We are waiting for you to come back!",
                    "Signin feedback message 3"
                  )
                });
                break;
              default:
                this.$refs.showFeedback.open("error", {
                  text: this.$t(
                    "signin:feedback.message4",
                    "We are very sorry - something went wrong. We cannot send you a link to set a password. Your account in athletes register is not active or you do not have a unique email address in the system or you do not have a valid yearly licence. If your yearly licence is paid, but you cannot still access KIPA, please contact our SUPPORT service. Organisers and administrators can sign in using their e-mail address that was used in the old KIPA site. If you do not remember your e-mail address, please contact our SUPPORT service. We are waiting for you to come back!",
                    "Signin feedback message 4"
                  )
                });
            }
          } else {
            switch (status) {
              case "ok":
                this.successCreated = true;
                this.$refs.showFeedback.open("success", {
                  text: this.$t(
                    "signin:feedback.message2",
                    "We have sent a link to reset your password to your email. It will expire in 60 minutes. If you do not receive an email in 5 minutes, please check your spam folder and then start again. Please check whether your licence number or email are correct: email should be the same as your email in old KIPA. If you still have problems, please contact our SUPPORT service.",
                    "Signin feedback message 2"
                  ),
                  dismiss: false
                });
                break;
              case "pick_hint":
                this.$refs.showFeedback.open("warn", {
                  text: this.$t(
                    "signin:signinform.identityHint.alert",
                    "More than one account found, please choose the account based on hint below"
                  )
                });
                break;
              default:
                this.$refs.showFeedback.open("error", {
                  text: this.$t(
                    "signin:feedback.message5",
                    "We are very sorry - something went wrong. We cannot send you a link to reset password. An email you have provided is not enough to identify your user account. Please, use your licence number to request a link. Organisers and administrators can sign in using their e-mail address that was used in the old KIPA site. If you do not remember your e-mail address, please contact our SUPPORT service.",
                    "Signin feedback message 5"
                  )
                });
            }
          }
        })
        .catch(error => {
          // Error
          this.loading = false;
          if (error.extraInfo == "not_found") {
            this.$refs.showFeedback.open("error", {
              text: this.$t(
                "signin:feedback.message6",
                "We are very sorry - something went wrong. We cannot send you a link to set a password. Your account in SENSE is not active or you do not have a valid yearly licence. Please, activate your account in SENSE or pay your yearly licence. We are waiting for you to come back!",
                "Signin feedback message 6"
              )
            });
          } else {
            this.$refs.showFeedback.showGraphqlError(error);
            this.$sentryThrowError.throwError(error, { level: "error" });
          }
        });
    }
  }
};
</script>
